/**
 * Cross-cutting auth actions that touch the API + the auth store.
 *
 * Kept out of the store itself because Zustand actions should be pure
 * state mutations — anything that talks to the network belongs in a
 * thin wrapper like this.
 */
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
/** Revoke every currently-issued token for the calling user, then sign
 *  the current tab out (which kicks off MSAL's logoutRedirect).
 *
 *  Industry pattern: "Sign out everywhere" — useful after a lost laptop,
 *  suspected credential compromise, or just paranoia. The backend bumps
 *  the user's tokens_revoked_before cutoff; every existing JWT (whose
 *  iat is necessarily <= now) fails the next validation as 'session
 *  revoked'. The user's own tabs / other devices will land on /login
 *  next time they make any request.
 *
 *  The redirect to /login happens via the existing auth store .logout()
 *  flow (MSAL logoutRedirect). We deliberately don't await the
 *  /auth/logout-all response before triggering the redirect — once the
 *  request is fired, the backend has the revocation regardless of
 *  whether the response makes it back to us.
 */
export async function signOutEverywhere() {
    try {
        await api.post('/auth/logout-all', {});
    }
    catch (err) {
        // Don't block sign-out on a transient network error — the user
        // is asking to be signed out and we'd rather over-sign-out than
        // leave them in an ambiguous state. Best-effort.
        console.warn('[auth] /auth/logout-all failed; continuing to local logout:', err);
    }
    useAuthStore.getState().logout();
}
